import { MobileActionDrawer } from '@components/MobileActionDrawer/MobileActionDrawer';
import { type AgentsSurface } from '@lib/analytics/agentsExit';
import { useLibraryVerbs } from '@lib/hooks/useLibraryVerbs';
import { isLibraryVerbId } from '@lib/verbs';
import { libraryFavorites } from '@stores/LibraryFavorites';
import { trackingStore } from '@stores/Tracking';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useRef } from 'react';
import {
  getLibraryPinAction,
  LIBRARY_MENU_ACTIONS,
  LibraryMenuAction,
} from '../libraryMenuActions';

type MobileLibraryContextDrawerProps = {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  libraryId: string;
  disableDelete?: boolean;
  onRename: () => void;
  onDelete: () => void;
  /**
   * Which page this drawer is mounted on, for the agents-app exit event. This
   * component is rendered from both the Libraries list and the library detail
   * header, and it has no other way to tell them apart — without it, exits from
   * inside a library would be miscounted as Libraries-list exits.
   */
  surface: Extract<AgentsSurface, 'libraries' | 'library'>;
  /**
   * Whether the surface that opened this drawer already shows the verb rail. When it
   * does, the drawer drops the two verbs and is purely "Manage library" — matching the
   * desktop kebab, which drops them for the same reason.
   *
   * Caller-supplied rather than derived here, because "is the rail on screen" is a fact
   * about the host: the Libraries grid has one (revealed at the viewport centre line),
   * the Libraries list has none, and the single-library header has none. Defaults to
   * false so a new mount keeps the verbs — the safe direction, since the failure mode is
   * a duplicated action rather than an unreachable one.
   */
  hasVerbRail?: boolean;
  /**
   * Whether to offer Pin/Unpin. Caller-supplied for the same reason as `hasVerbRail`: whether
   * pinning is reachable — and whether the pin store is even hydrated — is a fact about the host,
   * not something to infer here.
   *
   * It was briefly derived from `surface`, which is wrong twice over: that prop is documented as
   * existing for the agents-exit event, so re-bucketing telemetry would silently add or remove a
   * menu row; and the library-detail header, a plausible future place to pin the library you're
   * inside, couldn't opt in without editing this shared drawer.
   *
   * Defaults to false — the safe direction, since the failure mode is an absent action rather
   * than one that writes an unhydrated pin list over the user's real pins.
   */
  showPin?: boolean;
};

export const MobileLibraryContextDrawer = observer(function MobileLibraryContextDrawer({
  open,
  onClose,
  onOpen,
  libraryId,
  disableDelete,
  onRename,
  onDelete,
  surface,
  hasVerbRail = false,
  showPin = false,
}: MobileLibraryContextDrawerProps) {
  const { workWithAgent, findMoments } = useLibraryVerbs();
  // Short-circuited, so an instance that shows no pin never subscribes to the pin set and is
  // never re-rendered by somebody else's toggle.
  const isPinned = showPin && libraryFavorites.isFavorited(libraryId);

  // Mirror the desktop popup's (LibraryMenuButton) open/close telemetry exactly:
  //  - libraryMenuOpened on open
  //  - libraryMenuAction on an action tap
  //  - libraryMenuClosed ONLY on a plain dismiss (swipe/backdrop) — NOT on an action tap
  //    (the action event already covers it) and NOT on unmount (MUI's Menu fires neither).
  // MobileActionDrawer calls onAction() then onClose(), so handleAction sets the flag before
  // the resulting open→false edge is observed here.
  const prevOpen = useRef(false);
  const closedByAction = useRef(false);
  useEffect(() => {
    const wasOpen = prevOpen.current;
    // Update the edge-tracker unconditionally so it never desyncs from the real open state.
    prevOpen.current = open;
    if (open === wasOpen || !libraryId) return;
    if (open) {
      closedByAction.current = false;
      trackingStore.libraryMenuOpened({ libraryId });
    } else if (closedByAction.current) {
      closedByAction.current = false; // action tap already tracked; skip the close event
    } else {
      trackingStore.libraryMenuClosed({ libraryId });
    }
  }, [open, libraryId]);

  // The verbs stay only where nothing else offers them — see `hasVerbRail`. Mobile list
  // view has no rail (the row is 73px on a 360px screen and spends its width on the
  // thumbnail, name and ⋯), so there this drawer is the only way to reach either verb;
  // mobile grid view reveals a rail at the centre line, so there they'd be a duplicate.
  //
  // Pinning follows the same "only where nothing else offers it" logic, but lands on both
  // Libraries surfaces rather than one: the card's pin button reveals on hover (so touch
  // never sees it) and the table's pin column is desktop-only, leaving this drawer as
  // mobile's only route to pinning. Desktop's kebab correspondingly leaves it out — and see
  // `showPin` for why the host decides rather than this component.
  const actions = useMemo(() => {
    let items = LIBRARY_MENU_ACTIONS;
    if (hasVerbRail) {
      // Via `LIBRARY_VERB_IDS` so a third library verb can't be added to the catalog and left
      // duplicated here — see the same filter in `LibraryMenuButton`.
      items = items.filter((a) => !isLibraryVerbId(a.id));
    }
    if (disableDelete) items = items.filter((a) => a.id !== 'delete');
    return showPin ? [getLibraryPinAction(isPinned), ...items] : items;
  }, [hasVerbRail, disableDelete, isPinned, showPin]);

  const handleAction = (action: LibraryMenuAction) => {
    closedByAction.current = true;
    trackingStore.libraryMenuAction({ action, libraryId });

    const origin = { surface, control: 'library-mobile-drawer' };

    switch (action) {
      case 'work-with-agent':
        workWithAgent(libraryId, origin);
        break;
      case 'find-moments':
        findMoments(libraryId, origin);
        break;
      case 'rename':
        onRename();
        break;
      case 'delete':
        onDelete();
        break;
      case 'pin':
        // Also covered by the generic `libraryMenuAction` above, but that one can't say whether the
        // pin went on or off — and it counts a menu tap, not a pin change.
        libraryFavorites.toggleFavorite(libraryId, 'mobile-drawer');
        break;
    }
  };

  return (
    <MobileActionDrawer
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      title="Manage library"
      actions={actions}
      onAction={handleAction}
    />
  );
});
